// src/pages/recruiter-biodata-edit/components/BiodataPreview.jsx
import React from 'react';
import Icon from 'components/AppIcon';
import FilePreview from 'components/FilePreview';

const Label = ({ children }) => (
  <label className="text-sm font-medium text-text-secondary">{children}</label>
);

const Field = ({ label, value, mono }) => (
  <div>
    <Label>{label}</Label>
    <p className={`text-text-primary ${mono ? 'font-mono' : ''}`}>{value ?? 'Not specified'}</p>
  </div>
);

const formatDate = (dateString) => {
  if (!dateString) return 'Not specified';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return 'Not specified';
  return d.toLocaleDateString('en-GB');
};

const formatDocumentLabel = (type) => {
  const labels = {
    'passport_copy': 'FCL Passport or Identity Card',
    'next_of_kin_identification': 'Next of Kin Identity Card',
    'vaccine_certificate': 'Skilled Certificate',
    'medical_certificate': 'Medical Certificate (FWCMS)',
    'highest_qualification': 'Highest Qualification'
  };
  return labels[type] || type;
};

const BiodataPreview = ({ form, uploadedFiles }) => {
  const uploadedDocuments = uploadedFiles.filter(f => f.type === 'document');
  const uploadedPhotos = uploadedFiles.filter(f => f.type === 'photo');
  const existingDocuments = (form.documents || []).filter(Boolean);
  const existingPhotos = (form.photos || []).filter(Boolean);

  return (
    <div className="space-y-6">
      {/* Personal Information */}
      <div className="bg-white rounded-lg border border-secondary-200 p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="User" size={20} className="mr-2 text-primary" />
          Personal Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Field label="Full Name" value={form.fullName} />
            <Field label="Date of Birth" value={formatDate(form.dateOfBirth)} />
            <Field label="Gender" value={form.gender} />
            <Field label="Nationality" value={form.nationality} />
            <Field label="Place of Birth" value={form.placeOfBirth} />
            <Field label="Education Level" value={form.educationLevel} />
            <Field label="Religion" value={form.religion} />
          </div>
          <div className="space-y-4">
            <Field label="Passport Number" value={form.passportNo} mono />
            <Field label="Marital Status" value={form.maritalStatus} />
            <Field label="Height" value={form.heightCm ? `${form.heightCm} cm` : undefined} />
            <Field label="Weight" value={form.weightKg ? `${form.weightKg} kg` : undefined} />
            <Field label="VDR Type" value={form.vdrType} />
            <Field label="Medical Status" value={form.medicalStatus} />
            <Field label="Medical Checkup Date" value={formatDate(form.medicalCheckupDate)} />
            <Field label="Medical Certificate Expiry" value={formatDate(form.medicalCertificateExpiry)} />
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-lg border border-secondary-200 p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Mail" size={20} className="mr-2 text-primary" />
          Contact Information
        </h3>
        <div className="space-y-4">
          <Field label="Email" value={form.email} />
          <Field label="Contact Number" value={form.contactNo} />
          <Field label="Address" value={form.address} />
        </div>
      </div>

      {/* Next of Kin */}
      <div className="bg-white rounded-lg border border-secondary-200 p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Users" size={20} className="mr-2 text-primary" />
          Next of Kin Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Field label="Name" value={form.nextOfKinName} />
            <Field label="Relationship" value={form.nextOfKinRelationship} />
            <Field label="Contact Number" value={form.nextOfKinContactNo} />
          </div>
          <div className="space-y-4">
            <Field label="Identification No" value={form.nextOfKinIdentificationNo} mono />
            <Field label="Bank Account No" value={form.nextOfKinBankAccountNo} />
            <Field label="Bank Name" value={form.nextOfKinBankName} />
          </div>
        </div>
        <div className="mt-4">
          <Field label="Address" value={form.nextOfKinAddress} />
        </div>
      </div>

      {/* Skills & Experience */}
      {(form.skills || form.experience || form.remarks) && (
        <div className="bg-white rounded-lg border border-secondary-200 p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
            <Icon name="Briefcase" size={20} className="mr-2 text-primary" />
            Skills & Experience
          </h3>
          <div className="space-y-4">
            {form.skills && <Field label="Skills" value={form.skills} />}
            {form.experience && <Field label="Experience" value={form.experience} />}
            {form.remarks && <Field label="Remarks" value={form.remarks} />}
          </div>
        </div>
      )}

      {/* Documents */}
      {(existingDocuments.length > 0 || uploadedDocuments.length > 0) && (
        <div className="bg-white rounded-lg border border-secondary-200 p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
            <Icon name="FileText" size={20} className="mr-2 text-primary" />
            Documents ({existingDocuments.length + uploadedDocuments.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {existingDocuments.map((doc) => (
              <div
                key={doc.id}
                className="border border-secondary-200 rounded-lg p-3 flex items-center justify-between"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded overflow-hidden border border-secondary-200 bg-secondary-50 flex items-center justify-center">
                    <FilePreview
                      file={doc}
                      className="w-full h-full object-cover"
                      showFileName={false}
                    />
                  </div>
                  <div>
                    <div className="font-medium text-text-primary text-sm">
                      {formatDocumentLabel(doc.document_type)}
                    </div>
                    <div className="text-xs text-text-secondary truncate max-w-[200px]">
                      {doc.file_name}
                    </div>
                  </div>
                </div>
                <span className="text-xs text-success bg-success-100 px-2 py-1 rounded">Uploaded</span>
              </div>
            ))}
            {uploadedDocuments.map((doc, index) => (
              <div key={`new-${index}`} className="border border-secondary-200 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-secondary-100 rounded flex items-center justify-center">
                    <Icon name="FileText" size={20} className="text-secondary-600" />
                  </div>
                  <div>
                    <div className="font-medium text-text-primary text-sm">{formatDocumentLabel(doc.document_type)}</div>
                    <div className="text-xs text-text-secondary">{doc.file_name}</div>
                  </div>
                </div>
                <span className="text-xs text-warning bg-warning-100 px-2 py-1 rounded">Ready to Upload</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photos */}
      {(existingPhotos.length > 0 || uploadedPhotos.length > 0) && (
        <div className="bg-white rounded-lg border border-secondary-200 p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
            <Icon name="Image" size={20} className="mr-2 text-primary" />
            Photos ({existingPhotos.length + uploadedPhotos.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {existingPhotos.map((photo) => (
              <div key={photo.id} className="border border-secondary-200 rounded overflow-hidden">
                <div className="h-32 bg-secondary-100">
                  <FilePreview
                    file={photo}
                    className="w-full h-full object-cover"
                    showFileName={false}
                  />
                </div>
                <div className="p-2 text-xs text-text-secondary capitalize bg-secondary-50">
                  {photo.photo_type.replace(/_/g, ' ')}
                </div>
                <div className="px-2 pb-2">
                  <span className="text-xs text-success bg-success-100 px-2 py-1 rounded">Uploaded</span>
                </div>
              </div>
            ))}
            {uploadedPhotos.map((photo, index) => (
              <div key={`new-${index}`} className="border border-secondary-200 rounded overflow-hidden">
                <div className="h-32 bg-secondary-100 flex items-center justify-center">
                  <Icon name="Image" size={32} className="text-secondary-600" />
                </div>
                <div className="p-2 text-xs text-text-secondary capitalize bg-secondary-50">{photo.photo_type.replace(/_/g, ' ')}</div>
                <div className="px-2 pb-2">
                  <span className="text-xs text-warning bg-warning-100 px-2 py-1 rounded">Ready to Upload</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
        <div className="flex items-center">
          <Icon name="Info" size={16} className="mr-2 text-primary" />
          <span className="text-sm text-primary font-medium">
            {existingDocuments.length + existingPhotos.length > 0 
              ? `${existingDocuments.length + existingPhotos.length} existing files will be kept.` 
              : 'No existing files to modify.'}
            {uploadedDocuments.length + uploadedPhotos.length > 0 && 
              ` ${uploadedDocuments.length + uploadedPhotos.length} new files will be uploaded.`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default BiodataPreview;
